import { FaberApi, OrbitApi } from 'api'
import { AxiosPromise } from 'axios'
import {
  CreateUserPlanResponse,
  EditUserPlanDetailsResponse,
  GetActiveUserPlansType,
  GetAllFaberUsers,
  GetAllOtpackUserPlansType,
  GetOtpackHistoryType,
  GetPlansDetailsType,
  GetSubscriptionUserPlansType,
  GetUserPlansType,
  UserPlanByPaymentIdType
} from 'types/api/faber/oneTimePack.type'

class OneTimePackApi {
  static getAllFaberUsers(body: {
    searchCriteria: string
    searchValue: string
    faberToken: string
    limit: number
    planStatus: string
    offset: number
  }): AxiosPromise<GetAllFaberUsers> {
    let searchField = ''
    switch (body.searchCriteria) {
      case 'Email':
        searchField = 'email'
        break
      case 'MSISDN':
        searchField = 'msisdn'
        break
      case 'ICCID':
        searchField = 'iccid'
        break
      case 'Customer ID':
        searchField = 'userId'
        break
    }

    const searchValue = body.searchValue

    const requestBody = {
      [searchField]: searchValue,
      faberToken: body.faberToken,
      limit: body.limit,
      offset: body.offset,
      status: body.planStatus
    }

    return OrbitApi.httpPost(
      `/faber/user/search?&limit=${body.limit}&offset=${body.offset}&sort=desc`,
      {
        Authorization: `Bearer ${body.faberToken}`
      },
      requestBody
    )
  }

  static getAllInactiveOtpackUserPlans(
    userId: string,
    faberToken: string
  ): AxiosPromise<GetAllOtpackUserPlansType> {
    return FaberApi.httpGet(
      `/sim/ops/plan/${userId}?planStatuses=INACTIVE`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static getExternalAllInactiveOtpackUserPlans(
    userId: string,
    faberToken: string
  ): AxiosPromise<GetAllOtpackUserPlansType> {
    return FaberApi.httpGet(
      `/sim/ext/plan/${userId}?planStatuses=INACTIVE`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static getAllTerminatedOtpackUserPlans(
    userId: string,
    faberToken: string
  ): AxiosPromise<GetAllOtpackUserPlansType> {
    return FaberApi.httpGet(
      `/sim/ops/plan/${userId}?planStatuses=TERMINATED`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static getExternalAllTerminatedOtpackUserPlans(
    userId: string,
    faberToken: string
  ): AxiosPromise<GetAllOtpackUserPlansType> {
    return FaberApi.httpGet(
      `/sim/ext/plan/${userId}?planStatuses=TERMINATED`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static getAllActiveInactiveOtpackUserPlans(
    userId: string,
    faberToken: string,
    limit: number,
    offset: number
  ): AxiosPromise<GetUserPlansType> {
    return FaberApi.httpGet(
      `/sim/ops/plan/${userId}?limit=${limit}&offset=${offset}&excludeStatus=TERMINATED`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static getAllActiveOtpackUserPlans(
    userId: string,
    faberToken: string
  ): AxiosPromise<GetActiveUserPlansType> {
    return FaberApi.httpGet(
      `/sim/ops/plan/${userId}?planStatuses=ACTIVE`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static getExternalAllActiveOtpackUserPlans(
    userId: string,
    faberToken: string
  ): AxiosPromise<GetActiveUserPlansType> {
    return FaberApi.httpGet(
      `/sim/ext/plan/${userId}?planStatuses=ACTIVE`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static getActiveOtpUserPacks(
    userId: string,
    faberToken: string
  ): AxiosPromise<GetActiveUserPlansType> {
    return FaberApi.httpGet(
      `/sim/ops/plan/active/${userId}`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static getSubscriptionUserPacks(
    userId: string,
    faberToken: string
  ): AxiosPromise<GetSubscriptionUserPlansType> {
    return FaberApi.httpGet(
      `/subscriptions?userID=${userId}`,
      { Authorization: `Bearer ${faberToken}` },
      true
    )
  }

  static getOtpackPlanHistory(
    userPlanId: string,
    faberToken: string
  ): AxiosPromise<GetOtpackHistoryType> {
    return FaberApi.httpGet(
      `/sim/ops/planhistory/${userPlanId}`,
      { Authorization: `Bearer ${faberToken}` },
      true
    )
  }

  static getAllUserPlans(
    faberToken: string
  ): AxiosPromise<GetPlansDetailsType> {
    return FaberApi.httpGet(
      '/sim/ops/plan/count',
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }

  static createUserPlan(body: {
    userId: string
    catalogId: string
    paymentIntent: string
    faberToken: string
  }): AxiosPromise<CreateUserPlanResponse> {
    return FaberApi.httpPost(
      `/sim/ops/esim/package/provision/${body.userId}`,
      {
        Authorization: `Bearer ${body.faberToken}`
      },
      body
    )
  }

  static createUserPlanByICCID(body: {
    userId: string
    catalogId: string
    paymentIntent: string
    faberToken: string
    iccid: string
  }): AxiosPromise<CreateUserPlanResponse> {
    return FaberApi.httpPost(
      `/sim/ops/esim/package/provision/iccid/${body.userId}`,
      {
        Authorization: `Bearer ${body.faberToken}`
      },
      body
    )
  }

  static editUserDetails(body: {
    userId: string
    detailCategory: string
    newValue: string
    faberToken: string
  }): AxiosPromise<EditUserPlanDetailsResponse> {
    return OrbitApi.httpPatch(
      `/faber/user/details/${body.userId}`,
      {
        Authorization: `Bearer ${body.faberToken}`
      },
      body
    )
  }

  static getUserPlanByPaymentId(
    userId: string,
    paymentId: string,
    faberToken: string
  ): AxiosPromise<UserPlanByPaymentIdType> {
    return FaberApi.httpGet(
      `/sim/ops/plan/user/${userId}/payment/${paymentId}`,
      {
        Authorization: `Bearer ${faberToken}`
      },
      true
    )
  }
}

export default OneTimePackApi
